/**
 * Discovery AI - Universal Multi-Tenant Service Form Photo Uploader
 * 
 * Reusable across Vogue Rugs, Reza's, RugTown, Atlas, and all Discovery AI client themes.
 * - Uploads photos immediately in background on file selection (avoids Shopify reCAPTCHA loops)
 * - Shows client-side previews with live upload status & remove buttons
 * - Injects signed URLs directly into contact[body] and hidden contact fields
 * - Submits natively through Shopify's standard form pipeline
 */
(function() {
  'use strict';

  const MAX_FILES = 5;
  const MAX_FILE_SIZE_MB = 10;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'heic'];

  function getApiBaseUrl() {
    if (window.SERVICE_UPLOAD_API_URL) return window.SERVICE_UPLOAD_API_URL;
    if (window.DiscoveryAIApiBase) return window.DiscoveryAIApiBase;
    return 'https://api.productadvisorai.com';
  }

  function initServiceUploader(container) {
    if (!container || container.dataset.serviceUploaderInitialized) return;
    container.dataset.serviceUploaderInitialized = 'true';

    const form = container.closest('form');
    if (!form) return;

    const fileInput = container.querySelector('input[type="file"]');
    const dropzone = container.querySelector('.service-uploader-dropzone') || container;
    const previewGrid = container.querySelector('.service-uploader-previews') || createPreviewGrid(container);
    const errorEl = container.querySelector('.service-uploader-error') || createErrorElement(container);
    const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');

    // List of objects: { id, file, url, status: 'uploading' | 'done' | 'error' }
    let photosList = [];
    let isUploadingCount = 0;

    function showError(msg) {
      if (errorEl) {
        errorEl.textContent = msg;
        errorEl.style.display = 'block';
      } else {
        alert(msg);
      }
    }

    function clearError() {
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.style.display = 'none';
      }
    }

    function syncFormFields() {
      // Remove any previously injected inputs
      form.querySelectorAll('input[data-injected-photo]').forEach(el => el.remove());

      const successfulUrls = photosList.filter(p => p.status === 'done' && p.url).map(p => p.url);

      if (successfulUrls.length > 0) {
        // Notice for store owner
        const noticeInput = document.createElement('input');
        noticeInput.type = 'hidden';
        noticeInput.name = 'contact[Photo Download Notice]';
        noticeInput.value = 'Please download and save customer photos upon receipt. Secure viewing links expire in 7 days.';
        noticeInput.setAttribute('data-injected-photo', 'true');
        form.appendChild(noticeInput);
      }

      // 1. Inject individual photo links as hidden inputs
      successfulUrls.forEach((url, i) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `contact[Photo ${i + 1}]`;
        input.value = url;
        input.setAttribute('data-injected-photo', 'true');
        form.appendChild(input);
      });
    }

    function renderPreviews() {
      previewGrid.innerHTML = '';
      if (photosList.length === 0) {
        previewGrid.style.display = 'none';
        return;
      }
      previewGrid.style.display = 'flex';

      photosList.forEach((item) => {
        const thumb = document.createElement('div');
        thumb.className = 'service-uploader-thumb-item';
        thumb.style.cssText = 'position: relative; width: 68px; height: 68px; border-radius: 6px; overflow: hidden; border: 1px solid #3f3f4a; background: #202026; flex-shrink: 0;';

        const img = document.createElement('img');
        img.style.cssText = 'width: 100%; height: 100%; object-fit: cover; display: block;';
        
        if (item.file && item.file.type.startsWith('image/')) {
          img.src = URL.createObjectURL(item.file);
        } else {
          img.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="%23999" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>';
        }

        // Status badge
        const badge = document.createElement('div');
        badge.style.cssText = 'position: absolute; bottom: 2px; left: 2px; padding: 2px 4px; border-radius: 3px; font-size: 9px; font-weight: bold; line-height: 1;';
        
        if (item.status === 'uploading') {
          badge.style.background = 'rgba(0,0,0,0.6)';
          badge.style.color = '#fff';
          badge.innerHTML = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" style="animation: service-uploader-spin 1s linear infinite;"><circle cx="12" cy="12" r="10" stroke-dasharray="32" stroke-dashoffset="12"/></svg>';
        } else if (item.status === 'done') {
          badge.style.background = '#28a745';
          badge.style.color = '#fff';
          badge.innerHTML = '&#10003;';
        } else if (item.status === 'error') {
          badge.style.background = '#dc3545';
          badge.style.color = '#fff';
          badge.innerHTML = '!';
        }

        // Remove button
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.title = 'Remove photo';
        removeBtn.innerHTML = '&times;';
        removeBtn.style.cssText = 'position: absolute; top: 2px; right: 2px; width: 18px; height: 18px; background: rgba(0,0,0,0.65); color: #fff; border: none; border-radius: 50%; font-size: 13px; line-height: 1; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0;';
        
        removeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          photosList = photosList.filter(p => p.id !== item.id);
          syncFormFields();
          renderPreviews();
          clearError();
        });

        thumb.appendChild(img);
        thumb.appendChild(badge);
        thumb.appendChild(removeBtn);
        previewGrid.appendChild(thumb);
      });
    }

    async function uploadSingleFile(photoObj) {
      isUploadingCount++;
      try {
        const formData = new FormData();
        formData.append('files', photoObj.file);
        const tenant = container.dataset.tenant || 'rugtown';
        formData.append('tenant', tenant);

        const baseUrl = getApiBaseUrl();
        const uploadEndpoint = `${baseUrl}/api/upload-service-photo`;

        const response = await fetch(uploadEndpoint, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.detail || `Upload failed (Status ${response.status})`);
        }

        const data = await response.json();
        const urls = data.photo_urls || [];
        if (urls.length > 0) {
          photoObj.url = urls[0];
          photoObj.status = 'done';
          console.log('[ServiceFormUploader] Photo uploaded:', photoObj.url);
        } else {
          throw new Error('No URL returned from server');
        }
      } catch (err) {
        console.error('Photo upload error:', err);
        photoObj.status = 'error';
        showError(`Could not upload ${photoObj.file.name}: ${err.message}`);
      } finally {
        isUploadingCount--;
        syncFormFields();
        renderPreviews();
      }
    }

    function addFiles(filesList) {
      clearError();
      const filesArray = Array.from(filesList);

      for (const file of filesArray) {
        if (photosList.length >= MAX_FILES) {
          showError(`Maximum ${MAX_FILES} photos allowed.`);
          break;
        }

        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext) && !file.type.startsWith('image/')) {
          showError(`Invalid format "${file.name}". Please upload JPG, PNG, WEBP, or HEIC.`);
          continue;
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
          showError(`"${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB size limit.`);
          continue;
        }

        const isDuplicate = photosList.some(p => p.file.name === file.name && p.file.size === file.size);
        if (!isDuplicate) {
          const photoObj = {
            id: 'photo_' + Math.random().toString(36).substr(2, 9),
            file: file,
            url: null,
            status: 'uploading'
          };
          photosList.push(photoObj);
          renderPreviews();
          // Start background upload immediately
          uploadSingleFile(photoObj);
        }
      }

      if (fileInput) fileInput.value = '';
    }

    // Drag-and-drop events
    ['dragenter', 'dragover'].forEach(eventName => {
      dropzone.addEventListener(eventName, (e) => {
        e.preventDefault();
        e.stopPropagation();
        dropzone.classList.add('is-dragover');
      });
    });

    ['dragleave', 'drop'].forEach(eventName => {
      dropzone.addEventListener(eventName, (e) => {
        e.preventDefault();
        e.stopPropagation();
        dropzone.classList.remove('is-dragover');
      });
    });

    dropzone.addEventListener('drop', (e) => {
      if (e.dataTransfer && e.dataTransfer.files) {
        addFiles(e.dataTransfer.files);
      }
    });

    if (fileInput) {
      fileInput.multiple = true;
      fileInput.addEventListener('change', (e) => {
        if (e.target.files) {
          addFiles(e.target.files);
        }
      });
    }

    // Form submit listener
    form.addEventListener('submit', function(e) {
      // Clear native file input so binary is not posted to Shopify
      if (fileInput) fileInput.value = '';

      // If uploads are still running, wait for them
      if (isUploadingCount > 0) {
        e.preventDefault();
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.style.opacity = '0.7';
          submitBtn.innerHTML = `
            <span style="display:inline-flex; align-items:center; gap:8px;">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="animation: service-uploader-spin 1s linear infinite;"><circle cx="12" cy="12" r="10" stroke-dasharray="32" stroke-dashoffset="12"/></svg>
              Finishing photo upload...
            </span>
          `;
        }

        const checkInterval = setInterval(() => {
          if (isUploadingCount === 0) {
            clearInterval(checkInterval);
            syncFormFields();
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.style.opacity = '1';
            }
            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit();
            } else {
              form.submit();
            }
          }
        }, 300);
        return;
      }

      // Photos already uploaded -> sync hidden fields and proceed
      syncFormFields();
    });
  }

  function createPreviewGrid(container) {
    const grid = document.createElement('div');
    grid.className = 'service-uploader-previews';
    grid.style.cssText = 'display: none; gap: 10px; flex-wrap: wrap; margin-top: 12px;';
    container.appendChild(grid);
    return grid;
  }

  function createErrorElement(container) {
    const err = document.createElement('div');
    err.className = 'service-uploader-error';
    err.style.cssText = 'display: none; color: #fc7d02; font-size: 12px; margin-top: 8px; font-weight: 500;';
    container.appendChild(err);
    return err;
  }

  // Inject keyframe animation
  if (!document.getElementById('service-uploader-keyframes')) {
    const style = document.createElement('style');
    style.id = 'service-uploader-keyframes';
    style.textContent = `
      @keyframes service-uploader-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      .service-uploader-dropzone.is-dragover { border-color: #fc7d02 !important; background: rgba(252, 125, 2, 0.08) !important; }
    `;
    document.head.appendChild(style);
  }

  function initAll() {
    document.querySelectorAll('[data-service-uploader]').forEach(initServiceUploader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.initServiceUploader = initServiceUploader;
})();
